import React, { useEffect, useState } from "react";
import {
  Search,
  Mail,
  AlertTriangle,
  Activity,
  Loader2,
  Users,
  Database,
  Shield,
  Radar,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import api from "../../services/api";
import { toast } from "react-toastify";
import BreachDetailModal from "../common/BreachDetailModal";

const formatNum = (n) => (n || 0).toLocaleString();

const scoreColor = (rating) => {
  const map = {
    excellent: "#22c55e",
    good: "#84cc16",
    fair: "#f59e0b",
    poor: "#f97316",
    critical: "#ef4444",
  };
  return map[rating] || "#06b6d4";
};

const BreachCard = ({ breach, onClick }) => (
  <button
    onClick={onClick}
    className="group relative bg-[#12151C] rounded-xl p-5 border border-white/5 hover:border-cyan-500/30 transition-all text-left w-full cursor-pointer hover:shadow-[0_0_25px_rgba(6,182,212,0.15)] overflow-hidden"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-violet-500/0 group-hover:from-cyan-500/5 group-hover:to-violet-500/5 transition-all pointer-events-none" />

    <div className="relative flex items-center gap-3 mb-3">
      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1a1f2a] to-[#0f1218] border border-white/5 flex items-center justify-center overflow-hidden flex-shrink-0">
        {breach.logoPath ? (
          <img
            src={breach.logoPath}
            alt={breach.title}
            className="w-10 h-10 object-contain"
            onError={(e) => (e.target.style.display = "none")}
          />
        ) : (
          <span className="text-2xl font-bold text-cyan-400/60">
            {breach.title?.[0] || "?"}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[12px] font-semibold text-white truncate group-hover:text-cyan-300 transition-colors">
          {breach.title}
        </div>
        <div className="text-[10px] text-gray-500 font-mono tracking-wider">
          {breach.breachDate
            ? new Date(breach.breachDate)
                .toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
                .toUpperCase()
            : "—"}
        </div>
      </div>
    </div>
    <p className="relative text-xs text-gray-500 line-clamp-2 mb-3 min-h-[2rem]">
      {breach.description?.replace(/<[^>]*>/g, "") ||
        `In ${
          breach.breachDate ? new Date(breach.breachDate).getFullYear() : ""
        }, ${breach.title} experienced a data breach.`}
    </p>
    <div className="relative flex items-center justify-between text-xs pt-3 border-t border-white/5">
      <span className="flex items-center gap-1.5 text-gray-500 font-mono">
        <Users size={11} />
        {formatNum(breach.pwnCount)}
      </span>
      <span className="text-violet-400 font-mono text-[10px] uppercase tracking-wider">
        {(breach.dataClasses || []).length} types
      </span>
    </div>
  </button>
);

const DarkWebHome = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [history, setHistory] = useState([]);
  const [latestBreaches, setLatestBreaches] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("email");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [selectedBreach, setSelectedBreach] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [ov, hist, latest] = await Promise.all([
          api.getDarkWebOverview(),
          api.getDarkWebScoreHistory(30),
          api.getLatestGlobalBreaches(12),
        ]);
        setOverview(ov.overview);
        setHistory(hist.history || []);
        setLatestBreaches(latest.breaches || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue.trim()) return;
    try {
      setSearchLoading(true);
      setSearchResults(null);
      const res = await api.darkWebInstantSearch(searchValue, searchType);
      setSearchResults(res);
      if (res.count === 0) toast.success("No breaches found for this value.");
      else toast.warn(`Found ${res.count} breach(es).`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Search failed");
    } finally {
      setSearchLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0C0E12]">
        <Loader2 className="animate-spin text-cyan-400" size={48} />
      </div>
    );
  }

  const score = overview?.score?.score || 0;
  const rating = overview?.score?.rating || "fair";
  const scoreHex = scoreColor(rating);
  const scorePct = (score / 1000) * 100;
  const circumference = 2 * Math.PI * 90;
  const strokeOffset = circumference - (scorePct / 100) * circumference;

  return (
    <div
      className="min-h-screen bg-[#0C0E12] text-gray-300 p-6 lg:p-8"
      style={{
        backgroundImage: `
          radial-gradient(circle at 10% 0%, rgba(6,182,212,0.08), transparent 40%),
          radial-gradient(circle at 90% 100%, rgba(139,92,246,0.06), transparent 40%),
          linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)
        `,
        backgroundSize: "auto, auto, 40px 40px, 40px 40px",
      }}
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Radar size={14} className="text-cyan-400 animate-pulse" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-cyan-400 font-mono">
                Surveillance Console // Online
              </span>
            </div>
            <h1 className="text-3xl font-bold text-white">
              Welcome <span className="text-cyan-400">back</span>
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6">
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* DarkWeb Overview Card */}
            <div className="bg-[#12151C] rounded-2xl p-6 border border-white/5 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Shield size={12} className="text-cyan-400" />
                    <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-400 font-mono">
                      // Intelligence Feed
                    </span>
                  </div>
                  <h2 className="text-xl font-semibold text-white">
                    DarkWeb Overview
                  </h2>
                </div>
                <div className="text-[10px] font-mono text-gray-500 tracking-wider">
                  LIVE
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 ml-2 animate-pulse shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <OverviewStat
                  icon={<Mail size={18} />}
                  color="text-cyan-400"
                  glow="rgba(6,182,212,0.25)"
                  value={overview?.monitoredEmails}
                  label="Monitored Emails"
                />
                <OverviewStat
                  icon={<Search size={18} />}
                  color="text-amber-400"
                  glow="rgba(245,158,11,0.25)"
                  value={overview?.breachesFound}
                  label="Breaches Found"
                />
                <OverviewStat
                  icon={<AlertTriangle size={18} />}
                  color="text-red-400"
                  glow="rgba(239,68,68,0.25)"
                  value={overview?.newBreaches}
                  label="New Breaches"
                />
                <OverviewStat
                  icon={<Database size={18} />}
                  color="text-emerald-400"
                  glow="rgba(52,211,153,0.25)"
                  value={formatNum(overview?.dataScannedToday)}
                  label="Data Scanned"
                />
              </div>
            </div>

            {/* Instant Search Card */}
            <div className="bg-[#12151C] rounded-2xl p-6 border border-white/5 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Search size={12} className="text-cyan-400" />
                  <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-400 font-mono">
                    // Query Interface
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white">
                  Instant Search
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Probe the breach database for compromised records.
                </p>
              </div>

              <form onSubmit={handleSearch} className="flex gap-2 mb-3">
                <select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="px-3 py-2.5 bg-black/40 border border-white/10 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-cyan-500/50 focus:shadow-[0_0_15px_rgba(6,182,212,0.15)]"
                >
                  <option value="email">Email</option>
                  <option value="domain">Domain</option>
                </select>
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={
                    searchType === "email" ? "you@company.com" : "company.com"
                  }
                  className="flex-1 px-3 py-2.5 bg-black/40 border border-white/10 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-cyan-500/50 focus:shadow-[0_0_15px_rgba(6,182,212,0.15)] placeholder-gray-600"
                  required
                />
                <button
                  type="submit"
                  disabled={searchLoading}
                  className="px-5 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-400/60 text-cyan-300 rounded-lg font-mono text-xs tracking-wider uppercase disabled:opacity-50 flex items-center gap-2 transition-all hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]"
                >
                  {searchLoading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Search size={14} />
                  )}
                  Search
                </button>
              </form>

              <p className="text-[10px] text-gray-500 flex items-center gap-1.5 font-mono tracking-wider">
                🔒 Encrypted query — results for non-primary emails are
                redacted.
              </p>

              {searchResults && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  {searchResults.count === 0 ? (
                    <div className="text-sm text-emerald-300 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 font-mono">
                      ✓ No breaches found in the database for this value.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm font-mono text-red-400 flex items-center gap-2">
                        <AlertTriangle size={14} />
                        EXPOSED IN {searchResults.count} BREACH
                        {searchResults.count !== 1 ? "ES" : ""}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {searchResults.results.map((b) => (
                          <button
                            key={b.externalId}
                            onClick={() => setSelectedBreach(b)}
                            className="text-xs bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 hover:border-red-400/40 rounded-lg p-3 text-left transition-all"
                          >
                            <div className="font-semibold text-white">
                              {b.title}
                            </div>
                            <div className="text-gray-500 font-mono text-[10px] mt-0.5">
                              {b.breachDate
                                ? new Date(b.breachDate).toLocaleDateString()
                                : "—"}
                            </div>
                            <div className="text-gray-400 mt-1 text-[11px]">
                              {(b.dataClasses || []).slice(0, 3).join(", ")}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Global Latest Breaches */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Database size={12} className="text-violet-400" />
                    <span className="text-[10px] tracking-[0.2em] uppercase text-violet-400 font-mono">
                      // Global Feed
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-white">
                    Latest Breaches
                  </h2>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
                {latestBreaches.map((b) => (
                  <BreachCard
                    key={b.id}
                    breach={b}
                    onClick={() => setSelectedBreach(b)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN — Score + Risk */}
          <div className="space-y-6">
            <div className="bg-[#12151C] rounded-2xl p-6 border border-white/5 shadow-[0_0_30px_rgba(0,0,0,0.5)] relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-20 pointer-events-none"
                style={{
                  background: `radial-gradient(circle at 50% 50%, ${scoreHex}20, transparent 60%)`,
                }}
              />
              <div className="relative flex justify-between items-start mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Shield size={12} className="text-cyan-400" />
                    <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-400 font-mono">
                      // Risk Index
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-white">
                    Company Score
                  </h3>
                </div>
                <Link
                  to="/darkweb/history"
                  className="px-3 py-1 text-[10px] font-mono tracking-wider uppercase bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 rounded-md transition-all"
                >
                  History
                </Link>
              </div>

              <div className="relative flex items-center justify-center my-8">
                <div className="relative">
                  <svg width="220" height="220" viewBox="0 0 200 200">
                    <defs>
                      <filter id="glow">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                        <feMerge>
                          <feMergeNode in="coloredBlur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>
                    <circle
                      cx="100"
                      cy="100"
                      r="90"
                      stroke="#1f2937"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="90"
                      stroke={scoreHex}
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeOffset}
                      strokeLinecap="round"
                      transform="rotate(-90 100 100)"
                      filter="url(#glow)"
                      style={{ transition: "stroke-dashoffset 1s ease" }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-[10px] text-gray-500 font-mono tracking-widest uppercase -mb-1">
                      Score
                    </div>
                    <div
                      className="text-5xl font-bold font-mono"
                      style={{
                        color: scoreHex,
                        textShadow: `0 0 20px ${scoreHex}80`,
                      }}
                    >
                      {score}
                    </div>
                    <div
                      className="text-[10px] font-mono uppercase tracking-[0.3em] mt-1"
                      style={{ color: scoreHex }}
                    >
                      {rating}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#12151C] rounded-2xl p-6 border border-white/5 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Activity size={12} className="text-violet-400" />
                  <span className="text-[10px] tracking-[0.2em] uppercase text-violet-400 font-mono">
                    // Trend Analysis
                  </span>
                </div>
                <h3 className="text-base font-semibold text-white">
                  Risk Assessment
                </h3>
              </div>
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={history.map((h) => ({
                      date: new Date(h.computedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      }),
                      score: h.score,
                    }))}
                  >
                    <defs>
                      <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#06b6d4" />
                        <stop offset="100%" stopColor="#8b5cf6" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                    <XAxis
                      dataKey="date"
                      tick={{
                        fontSize: 10,
                        fill: "#64748b",
                        fontFamily: "monospace",
                      }}
                      stroke="#374151"
                    />
                    <YAxis
                      domain={[0, 100]}
                      ticks={[30, 40, 50, 60, 70, 80, 90, 100]}
                      tickFormatter={(v) => v}
                      tick={{
                        fontSize: 10,
                        fill: "#64748b",
                        fontFamily: "monospace",
                      }}
                      stroke="#374151"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#0C0E12",
                        border: "1px solid rgba(6,182,212,0.3)",
                        borderRadius: "8px",
                        fontSize: "12px",
                        fontFamily: "monospace",
                      }}
                      labelStyle={{ color: "#06b6d4" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="url(#lineGrad)"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "#06b6d4" }}
                      activeDot={{ r: 5, fill: "#06b6d4" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Shared Detail Modal */}
      {selectedBreach && (
        <BreachDetailModal
          breach={selectedBreach}
          onClose={() => setSelectedBreach(null)}
        />
      )}
    </div>
  );
};

const OverviewStat = ({ icon, color, glow, value, label }) => (
  <div className="flex items-center gap-3">
    <div
      className={`w-11 h-11 rounded-xl bg-black/40 border border-white/5 flex items-center justify-center ${color}`}
      style={{
        boxShadow: `inset 0 0 15px ${glow}, 0 0 10px ${glow}`,
      }}
    >
      {icon}
    </div>
    <div>
      <div className="text-lg font-bold text-white font-mono">{value ?? 0}</div>
      <div className="text-[10px] text-gray-500 font-mono tracking-wider uppercase">
        {label}
      </div>
    </div>
  </div>
);

export default DarkWebHome;
